'use client';

import { useMemo } from 'react';
import katex from 'katex';
import { marked } from 'marked';
import type { Tokens } from 'marked';

type MathToken = Tokens.Generic & { text: string };

marked.use({
  extensions: [
    {
      name: 'mathBlock',
      level: 'block' as const,
      start(src: string) {
        return src.indexOf('$$');
      },
      tokenizer(src: string): MathToken | undefined {
        const match = /^\$\$([\s\S]*?)\$\$/.exec(src);
        if (match?.[1] != null) return { type: 'mathBlock', raw: match[0], text: match[1].trim() };
        return undefined;
      },
      renderer(token: Tokens.Generic): string {
        return katex.renderToString((token as unknown as MathToken).text, {
          displayMode: true,
          throwOnError: false,
        });
      },
    },
    {
      name: 'mathInline',
      level: 'inline' as const,
      start(src: string) {
        return src.indexOf('$');
      },
      tokenizer(src: string): MathToken | undefined {
        if (src.startsWith('$$')) return undefined;
        const match = /^\$([^\n$]+?)\$/.exec(src);
        if (match?.[1] != null) return { type: 'mathInline', raw: match[0], text: match[1].trim() };
        return undefined;
      },
      renderer(token: Tokens.Generic): string {
        return katex.renderToString((token as unknown as MathToken).text, {
          displayMode: false,
          throwOnError: false,
        });
      },
    },
  ],
});

/**
 * `breaks` : un simple retour à la ligne devient un saut de ligne (`<br>`) au lieu d'être fondu dans
 * le paragraphe. Pour les textes saisis ligne par ligne (règle détaillée d'une carte).
 *
 * Pas de défilement sur le bloc entier : KaTeX déborde de 1-2 px en hauteur, ce qui rendait chaque
 * proposition « défilable » (dans les deux sens) sur téléphone. Le texte revient à la ligne ; seules
 * une formule centrée ou une table trop larges défilent, horizontalement.
 */
export function MarkdownMath({
  children,
  className,
  breaks = false,
}: {
  children: string;
  className?: string;
  breaks?: boolean;
}) {
  const html = useMemo(
    () => marked.parse(children, { async: false, breaks }) as string,
    [children, breaks],
  );
  return (
    <div
      className={`prose prose-sm max-w-none break-words [&_.katex-display]:overflow-x-auto [&_.katex-display]:overflow-y-hidden [&_.katex-display]:py-[0.15em] [&_table]:block [&_table]:max-w-full [&_table]:overflow-x-auto ${className ?? ''}`}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
